import { Global, css } from '@emotion/react';
import { ButtonVariants, Menu, MenuItem, Modal } from '@triumph/design-system';
import { useState } from 'react';

import { useToast } from '@/components/hooks/useToast';
import { DecisionDetailStatuses } from '@/components/pages/Applicant/Applicant.types';
import { useApplicantContext } from '@/components/providers/ApplicantContextProvider';
import { ProductNames } from '@/components/providers/ApplicantContextProvider/ApplicantContextProvider.types';
import { usePermissions } from '@/components/providers/PermissionsContextProvider';
import {
    useRefreshStatusLoadPayMutation,
    useResubmitApplicantMutation,
    useRetryAlloyFactoringMutation,
    useRetryDddMutation,
} from '@/components/shared/api/mutations/applicant';
import { useApplicantNextStepsQuery } from '@/components/shared/api/queries/applicant';
import { Feature, PERMISSION_DENIED_TITLE } from '@/components/shared/constants/features';

import { useQueryClient } from '@tanstack/react-query';
import { AbandonApplicantModal } from './AbandonApplicantModal';
import { ConfirmDebugActionDialog } from './ConfirmDebugActionDialog';
import { JsonViewer, MenuWrapper } from './MenuButton.StyledComponents';

const SUCCESS_TOAST_MESSAGE = 'Request completed successfully.';
const ERROR_TOAST_MESSAGE = 'Oops! We encountered an error. Please try submitting the form again.';

interface PendingDebugAction {
    label: string;
    run: () => Promise<void> | void;
}

export const MenuButton = () => {
    const { applicant, applicantId, applicantStatus } = useApplicantContext();
    const queryClient = useQueryClient();
    const { hasFeature } = usePermissions();
    const isNotErroredStatus = applicantStatus !== DecisionDetailStatuses.ABANDONED;

    const [showModal, setShowModal] = useState(false);
    const [showNextStepsModal, setShowNextStepsModal] = useState(false);
    const [showAbandonModal, setShowAbandonModal] = useState(false);
    const [pendingAction, setPendingAction] = useState<PendingDebugAction | null>(null);

    const {
        data: nextStepsData,
        isLoading: isNextStepsLoading,
        isFetching: isNextStepsFetching,
    } = useApplicantNextStepsQuery({
        applicantId: applicantId ?? '',
        enabled: showNextStepsModal,
    });

    const { mutateAsync: submit, isPending: isSubmitApplicantPending } =
        useResubmitApplicantMutation();

    const { mutateAsync: retryAlloyFactoring, isPending: isRetryAlloyFactoringPending } =
        useRetryAlloyFactoringMutation();

    const { mutateAsync: refreshStatusLoadPay, isPending: isRefreshStatusLoadPayPending } =
        useRefreshStatusLoadPayMutation();

    const { mutateAsync: retryDdd, isPending: isRetryDddPending } = useRetryDddMutation();

    const { toast, showToast } = useToast('menu-button-toast');

    const runWithToast = async (request: () => Promise<unknown>) => {
        try {
            await request();
            showToast('success', SUCCESS_TOAST_MESSAGE);
        } catch {
            showToast('error', ERROR_TOAST_MESSAGE);
        }
    };

    const handleClose = () => setShowModal(false);

    const handleViewJsonClick = () => {
        if (!hasFeature(Feature.DebugMenu)) return;
        setShowModal(true);
    };

    const handleCloseNextSteps = () => setShowNextStepsModal(false);
    const handleViewNextSteps = () => {
        if (!hasFeature(Feature.DebugMenu)) return;
        setShowNextStepsModal(true);
    };

    const handleReSubmitClick = async (products: ProductNames[]) => {
        if (!hasFeature(Feature.DebugMenu)) return;
        if (!isSubmitApplicantPending && applicant) {
            await runWithToast(() =>
                submit({
                    applicantId: applicant.id,
                    resetOverride: isNotErroredStatus,
                    products: products,
                }),
            );
        }
    };

    const handleRetryAlloyFactoringClick = async () => {
        if (!hasFeature(Feature.DebugMenu)) return;
        if (!isRetryAlloyFactoringPending && applicant) {
            await runWithToast(() =>
                retryAlloyFactoring({
                    applicantId: applicant.id,
                }),
            );
        }
    };

    const handleRetryDddClick = async () => {
        if (!hasFeature(Feature.DebugMenu)) return;
        if (!isRetryDddPending && applicant) {
            await runWithToast(() => retryDdd({ applicantId: applicant.id }));
        }
    };

    const handleRefreshStatusLoadPayClick = async () => {
        if (!hasFeature(Feature.DebugMenu)) return;
        if (!isRefreshStatusLoadPayPending && applicant) {
            await runWithToast(async () => {
                await refreshStatusLoadPay({
                    applicantId: applicant.id,
                });
                await queryClient.invalidateQueries({
                    queryKey: ['applicant', applicantId],
                });
            });
        }
    };

    const requestConfirm = (label: string, run: () => Promise<void> | void) => {
        if (!hasFeature(Feature.DebugMenu)) return;
        setPendingAction({ label, run });
    };

    const handleConfirmPending = () => {
        if (!pendingAction) return;
        const { run } = pendingAction;
        setPendingAction(null);
        void Promise.resolve(run()).catch(() => {
            /* toast surfaces the failure */
        });
    };

    const handleCancelPending = () => setPendingAction(null);

    const handleAbandonApplicantClick = () => {
        if (!hasFeature(Feature.DebugMenu)) return;
        setShowAbandonModal(true);
    };

    const handleAbandonSuccess = () => {
        // Abandon adds a status entry, which shows in activity as well as on the applicant.
        queryClient.invalidateQueries({ queryKey: ['applicant', applicantId] });
        queryClient.invalidateQueries({ queryKey: ['applicant-activity', applicantId] });
        showToast('success', SUCCESS_TOAST_MESSAGE);
    };

    return (
        <>
            <Global
                styles={css`
                    .MenuItem,
                    .MenuItem * {
                        text-align: left;
                    }
                `}
            />
            {toast}

            <span
                title={!hasFeature(Feature.DebugMenu) ? PERMISSION_DENIED_TITLE : undefined}
                style={{ height: '100%' }}
            >
                <MenuWrapper disabled={!hasFeature(Feature.DebugMenu)}>
                    <Menu label="DEBUG" variant={ButtonVariants.secondary} bold>
                        <MenuItem label="View Application in JSON" onClick={handleViewJsonClick} />
                        <MenuItem label="View Next Steps" onClick={handleViewNextSteps} />
                        {applicant?.factoringProduct ? (
                            <MenuItem
                                label="Re-submit Factoring Application"
                                onClick={() =>
                                    requestConfirm('Re-submit Factoring Application', () =>
                                        handleReSubmitClick([ProductNames.FACTORING]),
                                    )
                                }
                            />
                        ) : null}
                        {applicant?.loadPayProduct ? (
                            <MenuItem
                                label="Re-submit LoadPay Application"
                                onClick={() =>
                                    requestConfirm('Re-submit LoadPay Application', () =>
                                        handleReSubmitClick([ProductNames.LOADPAY]),
                                    )
                                }
                            />
                        ) : null}
                        {applicant?.tcsFuelCardProduct ? (
                            <MenuItem
                                label="Re-submit Fuel Card Application"
                                onClick={() =>
                                    requestConfirm('Re-submit Fuel Card Application', () =>
                                        handleReSubmitClick([ProductNames.TCSFUEL]),
                                    )
                                }
                            />
                        ) : null}
                        {applicant?.factoringProduct ? (
                            <MenuItem
                                label="Retry Alloy Requests for Factoring"
                                onClick={() =>
                                    requestConfirm(
                                        'Retry Alloy Requests for Factoring',
                                        handleRetryAlloyFactoringClick,
                                    )
                                }
                            />
                        ) : null}
                        {applicant?.factoringProduct || applicant?.loadPayProduct ? (
                            <MenuItem
                                label="Retry DDD Requests"
                                onClick={() =>
                                    requestConfirm('Retry DDD Requests', handleRetryDddClick)
                                }
                            />
                        ) : null}
                        {applicant?.loadPayProduct ? (
                            <MenuItem
                                label="Refresh LoadPay App Status"
                                onClick={handleRefreshStatusLoadPayClick}
                            />
                        ) : null}
                        <MenuItem label="Abandon Applicant" onClick={handleAbandonApplicantClick} />
                    </Menu>
                </MenuWrapper>
            </span>

            <ConfirmDebugActionDialog
                visible={Boolean(pendingAction)}
                actionLabel={pendingAction?.label ?? ''}
                onConfirm={handleConfirmPending}
                onClose={handleCancelPending}
            />

            <AbandonApplicantModal
                showModal={showAbandonModal}
                setShowModal={setShowAbandonModal}
                onSuccess={handleAbandonSuccess}
            />

            <Modal
                bodyContent={
                    <>
                        {/* FullStory privacy: mask entire applicant JSON to prevent PII from appearing in session recordings */}
                        <JsonViewer data-testid="json-viewer" className="me-is-sensitive-mask-me">
                            {JSON.stringify(applicant, null, 2)}
                        </JsonViewer>
                    </>
                }
                headerContent="Applicant"
                id="modal1"
                onClose={handleClose}
                visible={showModal}
                fullScreen
            />

            <Modal
                bodyContent={
                    <>
                        {isNextStepsLoading || isNextStepsFetching ? (
                            <div data-testid="next-steps-loading">Loading...</div>
                        ) : (
                            <JsonViewer data-testid="next-steps-viewer">
                                {JSON.stringify(nextStepsData, null, 2)}
                            </JsonViewer>
                        )}
                    </>
                }
                headerContent={`${applicant?.businessInfo.legalName} - Next Steps API`}
                id="deubg-next-steps-modal"
                onClose={handleCloseNextSteps}
                visible={showNextStepsModal}
                fullScreen
            />
        </>
    );
};
